import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FloatingField } from '@/components/ui/floating-field';
import { DateField } from '@/components/ui/date-field';
import { Combobox } from '@/components/ui/combobox';
import { StatusPill } from '@/components/ui/status-pill';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';
import { api, apiBlob, apiForm } from '@/lib/api';
import { useAuth } from '@/components/AuthProvider';
import {
  DOC_ACCEPT,
  DOC_MAX_BYTES,
  validateDocumentFileClient,
} from '@/lib/documentUploadRules';
import { DocumentUploadProgress } from '@/components/DocumentUploadProgress';

async function triggerBlobDownload(blob, fileName) {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'document';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  }
}

const DOC_TYPES = [
  { value: 'rate_con', label: 'Rate Confirmation', required: true },
  { value: 'bol', label: 'BOL (Bill of Lading)', required: true },
  { value: 'pod', label: 'POD (Proof of Delivery)', required: true },
  { value: 'fuel_receipt', label: 'Fuel Receipt', required: false },
  { value: 'lumper', label: 'Lumper Receipt', required: false },
  { value: 'other', label: 'Other', required: false },
];

const JURISDICTIONS = [
  { value: 'CA', label: 'California' },
  { value: 'TX', label: 'Texas' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NY', label: 'New York' },
];

function today() {
  return new Date().toISOString().slice(0, 10);
}

export function LoadDocumentsPanel({ loadId, loadNumber }) {
  const { user } = useAuth();
  const canMutateDocs =
    user?.role === 'admin' ||
    user?.role === 'dispatcher' ||
    user?.role === 'accountant' ||
    user?.role === 'driver';
  const [packet, setPacket] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [docType, setDocType] = useState('pod');
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState('');
  const [jurisdiction, setJurisdiction] = useState('TX');
  const [gallons, setGallons] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(today());
  const [amount, setAmount] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [busy, setBusy] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [deleteId, setDeleteId] = useState('');

  const refresh = useCallback(async () => {
    if (!loadId) return;
    const r = await api(`/api/v1/documents/packet/${loadId}`);
    if (r.status === 200) setPacket(r.data.packet);
  }, [loadId]);

  useEffect(() => {
    void refresh();
    void (async () => {
      const v = await api('/api/v1/fleet/vehicles');
      if (v.status === 200) setVehicles(v.data.vehicles ?? []);
    })();
  }, [refresh]);

  const isFuel = docType === 'fuel_receipt';
  const docs = packet?.documents ?? [];
  const deleteTarget = docs.find((d) => d.id === deleteId);

  const completeness = (() => {
    if (!packet?.slots?.length) return 0;
    const tracked = packet.slots.filter((s) => s.kind !== 'other');
    if (!tracked.length) return 0;
    return Math.round((tracked.filter((s) => s.present).length / tracked.length) * 100);
  })();

  const onPickFile = (next) => {
    if (busy) return;
    if (!next) {
      setFile(null);
      return;
    }
    const check = validateDocumentFileClient(next);
    if (!check.ok) {
      toast.error(check.error);
      setFile(null);
      return;
    }
    setFile(next);
  };

  const handleUpload = async () => {
    if (busy) return;
    if (!loadId) {
      toast.error('Select a load first');
      return;
    }
    if (!docType || !file) {
      toast.error('Please select a document type and file');
      return;
    }
    const check = validateDocumentFileClient(file);
    if (!check.ok) {
      toast.error(check.error);
      return;
    }
    if (isFuel) {
      const g = Number(gallons);
      if (!jurisdiction.trim() || !Number.isFinite(g) || g <= 0) {
        toast.error('Fuel receipt needs jurisdiction and gallons');
        return;
      }
    }
    setBusy(true);
    try {
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let binary = '';
      for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
      const contentBase64 = btoa(binary);
      const body = {
        loadId,
        kind: docType,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        contentBase64,
      };
      if (notes.trim()) body.notes = notes.trim();
      if (isFuel) {
        body.jurisdiction = jurisdiction.trim().toUpperCase();
        body.gallons = Number(gallons);
        body.purchaseDate =
          typeof purchaseDate === 'string'
            ? purchaseDate
            : purchaseDate
              ? new Date(purchaseDate).toISOString().slice(0, 10)
              : today();
        if (amount) body.amountDollars = Number(amount);
        if (vehicle) body.vehicleId = vehicle;
      }
      let r = await api('/api/v1/documents/upload-json', {
        method: 'POST',
        body,
        timeoutMs: 120000,
      });
      if (r.status === 404 || r.status === 405) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('loadId', loadId);
        fd.append('kind', docType);
        if (notes.trim()) fd.append('notes', notes.trim());
        if (isFuel) {
          fd.append('jurisdiction', jurisdiction.trim().toUpperCase());
          fd.append('gallons', gallons);
          if (amount) fd.append('amountDollars', amount);
          if (vehicle) fd.append('vehicleId', vehicle);
        }
        r = await apiForm('/api/v1/documents/upload', fd, { timeoutMs: 120000 });
      }
      if (r.status === 201) {
        toast.success('Document uploaded');
        setFile(null);
        setNotes('');
        setGallons('');
        setAmount('');
        if (r.data.packet) setPacket(r.data.packet);
        else await refresh();
      } else {
        toast.error(String(r.data?.error ?? 'Upload failed'));
      }
    } catch (err) {
      toast.error(err?.message ?? 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async (id, fileName) => {
    if (busy || downloadingId || removingId) return;
    setDownloadingId(id);
    try {
      const r = await apiBlob(`/api/v1/documents/${id}/download`, { timeoutMs: 120000 });
      if (r.status === 200 && r.blob) {
        await triggerBlobDownload(r.blob, r.fileName || fileName || 'document');
        return;
      }
      const signed = await api(`/api/v1/documents/${id}/download-url`);
      if (signed.status === 200 && signed.data?.url) {
        const a = document.createElement('a');
        a.href = signed.data.url;
        a.download = signed.data.fileName || fileName || 'document';
        a.rel = 'noopener';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }
      toast.error(String(r.data?.error || signed.data?.error || 'Could not download file'));
    } catch (err) {
      toast.error(err?.message ?? 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleRemove = async (id) => {
    if (busy || downloadingId || removingId) return;
    setRemovingId(id);
    try {
      const r = await api(`/api/v1/documents/${id}`, { method: 'DELETE' });
      if (r.status === 200) {
        toast.success('Document removed');
        await refresh();
      } else {
        toast.error(String(r.data?.error ?? 'Delete failed'));
      }
    } finally {
      setRemovingId(null);
    }
  };

  const vehicleOpts = vehicles.map((v) => ({
    value: v.id,
    label: v.unitNumber || v.id,
  }));

  const slotList =
    packet?.slots?.filter((s) => s.kind !== 'other' || s.count > 0) ??
    DOC_TYPES.map((t) => ({
      kind: t.value,
      label: t.label,
      required: t.required,
      present: docs.some((d) => d.kind === t.value),
      count: docs.filter((d) => d.kind === t.value).length,
    }));

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icons.FileText className="h-5 w-5 text-primary" />
            <CardTitle>Load Documents{loadNumber ? ` — ${loadNumber}` : ''}</CardTitle>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Completeness</p>
              <p className="text-lg font-bold font-mono">{completeness}%</p>
            </div>
            <StatusPill
              status={packet?.readyForSettlement || completeness === 100 ? 'ready' : 'missing_pod'}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {slotList.map((slot) => {
            const uploaded = slot.present;
            const meta = DOC_TYPES.find((t) => t.value === slot.kind);
            return (
              <div
                key={slot.kind}
                className={`flex items-center gap-2 p-2 rounded-md text-xs ${
                  uploaded
                    ? 'bg-success/10 text-success'
                    : slot.required || meta?.required
                      ? 'bg-destructive/10 text-destructive'
                      : 'bg-muted text-muted-foreground'
                }`}
              >
                {uploaded ? (
                  <Icons.CheckCircle2 className="h-3.5 w-3.5" />
                ) : (
                  <Icons.Circle className="h-3.5 w-3.5" />
                )}
                <span className="font-medium">
                  {slot.label || meta?.label || slot.kind}{' '}
                  {(slot.required || meta?.required) && (
                    <span className="text-destructive">*</span>
                  )}
                </span>
                <span className="ml-auto">{slot.count ?? 0}</span>
              </div>
            );
          })}
        </div>

        {canMutateDocs ? (
          <div className="space-y-4 border-t pt-4" aria-busy={busy}>
            <h4 className="font-semibold text-sm">Upload New Document</h4>
            <p className="text-xs text-muted-foreground">
              PDF, PNG, or JPG only · max {DOC_MAX_BYTES / (1024 * 1024)}MB · scanned on upload
            </p>

            <DocumentUploadProgress active={busy} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Document Type <span className="text-destructive">*</span>
                </label>
                <Select value={docType} onValueChange={setDocType} disabled={busy}>
                  <SelectTrigger data-testid="doc-type-select">
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOC_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label} {t.required && '*'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  File <span className="text-destructive">*</span>
                </label>
                <div
                  className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                    busy
                      ? 'opacity-60 pointer-events-none cursor-not-allowed'
                      : 'hover:border-primary cursor-pointer'
                  }`}
                  onClick={() => {
                    if (!busy) {
                      document
                        .getElementById(`file-upload-${loadId || loadNumber || 'doc'}`)
                        ?.click();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (busy) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      document
                        .getElementById(`file-upload-${loadId || loadNumber || 'doc'}`)
                        ?.click();
                    }
                  }}
                  role="button"
                  tabIndex={busy ? -1 : 0}
                  aria-disabled={busy}
                >
                  <input
                    type="file"
                    accept={DOC_ACCEPT}
                    onChange={(e) => {
                      onPickFile(e.target.files?.[0] ?? null);
                      e.target.value = '';
                    }}
                    className="hidden"
                    id={`file-upload-${loadId || loadNumber || 'doc'}`}
                    data-testid="doc-file-input"
                    disabled={busy}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <Icons.Paperclip className="h-4 w-4" />
                    {busy
                      ? 'Upload in progress…'
                      : file
                        ? file.name
                        : 'Click to choose PDF, PNG, or JPG'}
                  </div>
                </div>
              </div>
            </div>

            {isFuel && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg border">
                <div className="md:col-span-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                    Fuel Receipt Details
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground">Jurisdiction</label>
                  <Combobox
                    options={JURISDICTIONS}
                    value={jurisdiction}
                    onChange={setJurisdiction}
                    placeholder="Select state"
                    disabled={busy}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground">Vehicle</label>
                  <Combobox
                    options={vehicleOpts}
                    value={vehicle}
                    onChange={setVehicle}
                    placeholder="Optional unit"
                    disabled={busy}
                  />
                </div>
                <FloatingField
                  label="Gallons"
                  type="number"
                  value={gallons}
                  onChange={(e) => setGallons(e.target.value)}
                  required
                  disabled={busy}
                />
                <FloatingField
                  label="Amount ($)"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  disabled={busy}
                />
                <div className="md:col-span-2 space-y-2">
                  <label className="text-xs text-muted-foreground">Purchase Date</label>
                  <DateField value={purchaseDate} onChange={setPurchaseDate} disabled={busy} />
                </div>
              </div>
            )}

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional context..."
                rows={2}
                disabled={busy}
                className="mt-2 w-full px-3 py-2 border rounded-md text-sm resize-none outline-none focus:ring-2 focus:ring-ring bg-background disabled:opacity-60"
              />
            </div>

            <div className="flex justify-end">
              {busy ? (
                <Button disabled className="min-w-[160px]" data-testid="upload-doc-button">
                  <Icons.Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading…
                </Button>
              ) : (
                <Button
                  onClick={() => void handleUpload()}
                  data-testid="upload-doc-button"
                  disabled={!loadId || !docType || !file}
                >
                  <Icons.Upload className="h-4 w-4 mr-2" />
                  Upload to Load
                </Button>
              )}
            </div>
          </div>
        ) : null}

        {docs.length > 0 ? (
          <div className="border-t pt-4">
            <h4 className="font-semibold text-sm mb-3">Uploaded Documents ({docs.length})</h4>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase">
                      Kind
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase">
                      File
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase">
                      Details
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-muted-foreground uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {docs.map((doc) => (
                    <tr key={doc.id} className="hover:bg-muted/30">
                      <td className="px-3 py-2">
                        <StatusPill status={doc.kind} />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{doc.fileName}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {doc.kind === 'fuel_receipt' && doc.fuel
                          ? `${doc.fuel.jurisdiction ?? '?'} · ${doc.fuel.gallons ?? '?'} gal`
                          : doc.createdAt
                            ? new Date(doc.createdAt).toLocaleString()
                            : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={Boolean(busy || downloadingId || removingId)}
                            onClick={() => void handleDownload(doc.id, doc.fileName)}
                          >
                            {downloadingId === doc.id ? (
                              <Icons.Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Icons.Download className="h-3 w-3" />
                            )}
                          </Button>
                          {canMutateDocs ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={Boolean(busy || downloadingId || removingId)}
                              onClick={() => setDeleteId(doc.id)}
                            >
                              {removingId === doc.id ? (
                                <Icons.Loader2 className="h-3 w-3 animate-spin text-destructive" />
                              ) : (
                                <Icons.Trash2 className="h-3 w-3 text-destructive" />
                              )}
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="border-t pt-4 text-center py-6 text-sm text-muted-foreground">
            No documents on this load yet
          </div>
        )}

        <ConfirmDialog
          open={Boolean(deleteId)}
          onOpenChange={(open) => !open && setDeleteId('')}
          title="Delete document?"
          description={`Permanently remove ${deleteTarget?.fileName || 'this document'}?`}
          confirmLabel={removingId ? 'Deleting…' : 'Delete'}
          variant="destructive"
          onConfirm={() => {
            if (deleteId) void handleRemove(deleteId);
            setDeleteId('');
          }}
        />
      </CardContent>
    </Card>
  );
}
